import {inject} from '@angular/core';
import {
  catchError,
  concatMap,
  exhaustMap,
  finalize,
  map,
  mergeMap,
  of, switchMap,
  withLatestFrom
} from 'rxjs';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {Store} from "@ngrx/store";
import {CommonsFacadeService} from "../../../commons/angular/state/commons/commons-facade.service";
import {OrdersHttpService} from "../service/orders-http.service";
import {OrderModel} from "../../models/order.model";
import {OrdersActions, OrdersAPiActions, OrdersChannelActions} from "./orders.action";
import {ordersFeature} from "./orders.state";
import {OrdersChannelService} from "../service/orders-channel.service";


export const getInfiniteScrollOrders = createEffect(
  (actions$ = inject(Actions),
   ordersHttpService = inject(OrdersHttpService),
   ordersStore = inject(Store<OrderModel>),
   commonsFacadeService = inject(CommonsFacadeService)
  ) => {
    return actions$.pipe(
      ofType(OrdersAPiActions.getInfiniteScrollOrders),
      concatMap(payload =>
        of(payload).pipe(
          withLatestFrom(ordersStore.select(ordersFeature.selectCursor()))
        )
      ),
      mergeMap(([payload, cursor]) => {
        commonsFacadeService.setLoaderOn();
        let paramsCopy = {...payload.params}
        if (!!cursor) {
          paramsCopy.cursor = cursor;
        }
        return ordersHttpService.getOrders(paramsCopy).pipe(
          map((res: {
            list: OrderModel[]
            cursor: string
          }) => OrdersAPiActions.getInfiniteScrollOrdersSuccess({
            orders: res.list,
            cursor: res.cursor,
            isSubscribeToChannel: payload.isSubscribeToChannel
          })),
          catchError((error: {
            message: string
          }) => of(OrdersAPiActions.getInfiniteScrollOrdersFailure({error}))),
          finalize(() => {
            commonsFacadeService.setLoaderOff()
          })
        )
      })
    );
  },
  {functional: true}
);

export const getOrders = createEffect(
  (actions$ = inject(Actions),
   ordersHttpService = inject(OrdersHttpService),
   commonsFacadeService = inject(CommonsFacadeService)
  ) => {
    return actions$.pipe(
      ofType(OrdersAPiActions.getOrders),
      switchMap((payload) => {
        commonsFacadeService.setLoaderOn();
        return ordersHttpService.getOrders(payload.params).pipe(
          map((res: {
            list: OrderModel[]
            cursor: string
          }) => OrdersAPiActions.getOrdersSuccess({
            orders: res.list,
            isSubscribeToChannel: payload.isSubscribeToChannel
          })),
          catchError((error: {
            message: string
          }) => of(OrdersAPiActions.getOrdersFailure({error}))),
          finalize(() => {
            commonsFacadeService.setLoaderOff()
          })
        )
      })
    );
  },
  {functional: true}
);

export const getOrdersSuccess = createEffect(
  (actions$ = inject(Actions),
   ordersChannelService = inject(OrdersChannelService)) => {
    return actions$.pipe(
      ofType(OrdersAPiActions.getOrdersSuccess),
      map((payload) => {
        if (payload.isSubscribeToChannel) {
          const orderIds = payload.orders.map(r => r.id);
          orderIds.forEach(orderId => ordersChannelService.subscribeById(orderId));
        }
      })
    );
  },
  {functional: true, dispatch: false}
);

export const getInfiniteScrollOrdersSuccess = createEffect(
  (actions$ = inject(Actions),
   ordersChannelService = inject(OrdersChannelService)) => {
    return actions$.pipe(
      ofType(OrdersAPiActions.getInfiniteScrollOrdersSuccess),
      map((payload) => {
        if (payload.isSubscribeToChannel) {
          const orderIds = payload.orders.map(r => r.id);
          orderIds.forEach(orderId => ordersChannelService.subscribeById(orderId));
        }
      })
    );
  },
  {functional: true, dispatch: false}
);

export const subscribeSetOrdersChannels = createEffect(
  (actions$ = inject(Actions),
   ordersChannelService = inject(OrdersChannelService)) => {
    return actions$.pipe(
      ofType(OrdersActions.setOrders),
      map((payload) => {
        const orderIds = payload.orders.map(r => r.id);
        orderIds.forEach(orderId => ordersChannelService.subscribeById(orderId));
      })
    );
  },
  {functional: true, dispatch: false}
);

export const subscribeAllOrdersChannel = createEffect(
  (actions$ = inject(Actions),
   ordersChanelService = inject(OrdersChannelService)) => {
    return actions$.pipe(
      ofType(OrdersChannelActions.subscribe),
      mergeMap(payload =>
        ordersChanelService.getChannelsValues().pipe(
          map((notification) => {
            return OrdersChannelActions.notificationReceived({notification: notification})
          })
        )
      )
    );
  },
  {functional: true}
);

export const subscribeAllPartialOrdersChannel = createEffect(
  (actions$ = inject(Actions),
   ordersChanelService = inject(OrdersChannelService)) => {
    return actions$.pipe(
      ofType(OrdersChannelActions.subscribePartial),
      mergeMap(payload =>
        ordersChanelService.getPartialChannelsValues().pipe(
          map((notification) => {
            return OrdersChannelActions.partialNotificationReceived({notification: notification})
          })
        )
      )
    );
  },
  {functional: true}
);


export const unsubscribeAllOrderChannels = createEffect(
  (actions$ = inject(Actions),
   ordersChanelService = inject(OrdersChannelService)) => {
    return actions$.pipe(
      ofType(OrdersChannelActions.unsubscribe, OrdersActions.removeAll, OrdersActions.clearStore),
      map(payload => {
          ordersChanelService.unSubscribeAll();
          return OrdersChannelActions.unsubscribeSuccess();
        }
      )
    );
  },
  {functional: true}
);

export const unsubscribeOrderChannels = createEffect(
  (actions$ = inject(Actions),
   ordersChanelService = inject(OrdersChannelService)) => {
    return actions$.pipe(
      ofType(OrdersActions.removeOrders),
      map(payload => {
          payload.ids?.forEach(orderId => ordersChanelService.unSubscribeById(orderId));
          return OrdersChannelActions.unsubscribeSuccess();
        }
      )
    );
  },
  {functional: true}
);





